import moment from 'moment';
import React, { memo, useCallback, useMemo, useState } from 'react';

import { playTabClickSound } from '@wg/web2clientapi/sound';
import {
  Checkbox,
  ClanMember,
  ContextMenu,
  DivTooltip,
  Interpolate,
  Spinner,
  TableBodyCell,
  TableRow,
  TooltipBody as UIKitTooltipBody,
} from '@wg/wows-react-uikit';

import { ACCOUNT_BAN_TYPES, isInGame } from '~/constants';
import { clanIsFull } from '~/helpers/clan';
import { getPositionY } from '~/helpers/contextMenu';
import { isoToFormattedLocalDate, isoToFormattedLocalTime } from '~/helpers/datetime';
import { floats, percent, thousands } from '~/helpers/formatting';
import { t } from '~/helpers/localization';

import { ButtonAction, RankCell, StatisticsValue, TooltipInfo } from '~/UIKit';
import { TooltipBody, TooltipFooter } from '~/UIKit/components';

import styles from './ApplicationsClanMemberTableItem.scss';

import type { IProps as IContextMenuProps } from '@wg/wows-react-uikit/components/ContextMenu/ContextMenu';
import type { IClanApplication } from '~/Actions/ActionApplications';
import type { ClanInfoType } from '~/Actions/ActionClanProfile';
import type { ClanPreloaded } from '~/types/declaration';

const getTooltipBody = (application: IClanApplication) => (
  <TooltipBody>
    <Interpolate
      useDangerouslySetInnerHTML={true}
      str={t('Время для принятия решения по заявке истекает %(date)s&nbsp;в&nbsp;%(time)s.')}
      date={isoToFormattedLocalDate(application.expires_at)}
      time={isoToFormattedLocalTime(application.expires_at)}
      parentTag={'p'}
    />
    <p>
      <Interpolate
        useDangerouslySetInnerHTML={true}
        str={t('Заявка отправлена %(date)s&nbsp;в&nbsp;%(time)s.')}
        date={isoToFormattedLocalDate(application.created_at)}
        time={isoToFormattedLocalTime(application.created_at)}
      />
    </p>
  </TooltipBody>
);

const getDeclineBtnTooltipContent = (application: IClanApplication) => (
  <div>
    {getTooltipBody(application)}
    <TooltipFooter mouseIcon="left">{t('Отклонить заявку')}</TooltipFooter>
  </div>
);

interface ApplicationsClanMemberTableItemProps {
  application: IClanApplication;
  isProcessing: boolean;
  isChatDenied: boolean;
  isChecked?: boolean;
  clan: ClanInfoType | ClanPreloaded;
  onAcceptedApplication: (application: IClanApplication) => void;
  onDeclinedApplication: (application: IClanApplication) => void;
  openAccountProfile: (id: string | number) => void;
  openChatWindow: (id: string | number, name: string) => void;
  onApplicationsPlayerTick?: (id: number) => void;
}

const ApplicationsClanMemberTableItem = ({
  application,
  isProcessing,
  isChatDenied,
  isChecked,
  clan,
  onAcceptedApplication,
  onDeclinedApplication,
  openAccountProfile,
  openChatWindow,
  onApplicationsPlayerTick,
}: ApplicationsClanMemberTableItemProps) => {
  const [isContextMenuActive, setIsContextMenuActive] = useState(false);
  const [contextMenuContext, setContextMenuContext] = useState<IContextMenuProps['context']>([]);
  const [contextMenuPosition, setContextMenuPosition] = useState<{ x: number; y: number }>({ x: 0, y: 0 });

  const account = application.account;
  const statistics = application.statistics;

  const isClanCooldown = useMemo(() => {
    return moment(account.in_clan_cooldown_till).isAfter(moment().utc());
  }, [account.in_clan_cooldown_till]);

  const isFullClan = useMemo(() => clanIsFull(clan), [clan]);

  const hasBan = useMemo(
    () => application.is_banned || account.is_banned || account.bans.length > 0,
    [account.bans.length, account.is_banned, application.is_banned],
  );

  const hasPermanentBan = useMemo(() => {
    return account.bans.includes(ACCOUNT_BAN_TYPES.PERMANENT) || application.is_banned;
  }, [account.bans, application.is_banned]);

  const acceptBtnTooltipText = useMemo(() => {
    const hasClanCooldown = moment(account.in_clan_cooldown_till).isAfter(moment().utc());
    const isFullClan = clanIsFull(clan);

    let acceptTooltip: string | null = null;

    if (isFullClan) {
      acceptTooltip = t('В клане сейчас нет мест');
    } else if (hasPermanentBan) {
      acceptTooltip = t('Аккаунт игрока заблокирован');
    } else if (hasClanCooldown) {
      acceptTooltip = t('Игрок недавно покинул клан. Вы сможете принять его в клан начиная с %(date)s&nbsp;%(time)s.', {
        date: isoToFormattedLocalDate(account.in_clan_cooldown_till),
        time: isoToFormattedLocalTime(account.in_clan_cooldown_till),
      }).replace('&nbsp;', '\u00a0');
    }

    return acceptTooltip;
  }, [account.in_clan_cooldown_till, clan, hasPermanentBan]);

  const contextMenu = useMemo<IContextMenuProps['context']>(() => {
    const banMsg = hasPermanentBan ? t('Аккаунт игрока заблокирован') : undefined;
    const baseItems: IContextMenuProps['context'][number]['items'] = [];

    if (isInGame) {
      baseItems.push({
        name: t('Отправить сообщение'),
        value: 'ACCOUNT_MESSAGE',
        disabled: hasPermanentBan || isChatDenied,
        tooltip: banMsg,
      });
    }

    baseItems.push(
      {
        name: t('Профиль игрока'),
        value: 'ACCOUNT_PROFILE',
        disabled: hasPermanentBan,
        tooltip: banMsg,
      },
      {
        name: t('Принять в клан'),
        value: 'ACCEPT_APPLICATION',
        disabled: isProcessing || !!acceptBtnTooltipText,
        tooltip: acceptBtnTooltipText ?? undefined,
      },
      {
        name: t('Отклонить заявку'),
        disabled: isProcessing,
        value: 'DECLINE_APPLICATION',
        tooltip: undefined,
      },
    );

    return [
      {
        isNeedDivider: false,
        items: baseItems,
      },
    ];
  }, [acceptBtnTooltipText, hasPermanentBan, isChatDenied, isProcessing]);

  const onCheckboxChange = useCallback(() => {
    if (onApplicationsPlayerTick) {
      onApplicationsPlayerTick(application.id);
    }
  }, [application.id, onApplicationsPlayerTick]);

  const onContextMenu = useCallback<React.MouseEventHandler<HTMLElement>>(
    (event) => {
      const clientY = getPositionY(event, contextMenu);

      setContextMenuContext(contextMenu);
      setContextMenuPosition({
        x: event.clientX,
        y: clientY,
      });
    },
    [contextMenu],
  );

  const onContextMenuStateChange = useCallback((active: boolean) => {
    setIsContextMenuActive(active);
  }, []);

  const onContextMenuItemClick = useCallback<IContextMenuProps['onItemClick']>(
    (action) => {
      switch (action) {
        case 'ACCOUNT_PROFILE': {
          openAccountProfile(account.id);
          break;
        }

        case 'ACCOUNT_MESSAGE': {
          if (!isChatDenied) {
            void playTabClickSound();
            openChatWindow(account.id, account.name);
          }
          break;
        }

        case 'ACCEPT_APPLICATION': {
          onAcceptedApplication(application);
          break;
        }

        case 'DECLINE_APPLICATION': {
          onDeclinedApplication(application);
          break;
        }
      }
    },
    [
      account.id,
      account.name,
      application,
      isChatDenied,
      onAcceptedApplication,
      onDeclinedApplication,
      openAccountProfile,
      openChatWindow,
    ],
  );

  const onTableRowClick = useCallback(() => {
    if (isChatDenied) {
      return;
    }

    void playTabClickSound();
    openChatWindow(account.id, account.name);
  }, [account.id, account.name, isChatDenied, openChatWindow]);

  const acceptBtnTooltipContent = useMemo(() => {
    if (isContextMenuActive) {
      return null;
    }

    if (acceptBtnTooltipText) {
      return (
        <div>
          <TooltipInfo>{acceptBtnTooltipText}</TooltipInfo>
          {getTooltipBody(application)}
        </div>
      );
    }

    return (
      <div>
        {getTooltipBody(application)}
        <TooltipFooter mouseIcon="left">{t('Принять в клан')}</TooltipFooter>
      </div>
    );
  }, [acceptBtnTooltipText, application, isContextMenuActive]);

  const declineBtnTooltipContent = useMemo(() => {
    if (isContextMenuActive) {
      return null;
    }

    return getDeclineBtnTooltipContent(application);
  }, [application, isContextMenuActive]);

  const isDisableAccept = isClanCooldown || hasBan || isFullClan;

  const comment = application?.comment ?? null;
  const isHiddenStatistics = application.is_hidden_statistics;

  let actionCell: React.ReactNode = null;

  if (isProcessing) {
    actionCell = (
      <TableBodyCell modify="right">
        <Spinner />
      </TableBodyCell>
    );
  } else {
    actionCell = (
      <TableBodyCell modify="right">
        <ButtonAction
          action="ok"
          isDisabled={isDisableAccept}
          onClick={() => {
            onAcceptedApplication(application);
          }}
          tooltipContent={acceptBtnTooltipContent}
        />
        <ButtonAction
          action="cancel"
          isDisabled={false}
          onClick={() => {
            onDeclinedApplication(application);
          }}
          tooltipContent={declineBtnTooltipContent}
        />
      </TableBodyCell>
    );
  }

  return (
    <TableRow isHovering isActive={isContextMenuActive} onContextMenu={onContextMenu} onClick={onTableRowClick}>
      <TableBodyCell modify="check">
        <Checkbox onChange={onCheckboxChange} isChecked={isChecked} />
      </TableBodyCell>

      <ContextMenu
        context={contextMenuContext}
        onContextMenuStateChange={onContextMenuStateChange}
        onItemClick={onContextMenuItemClick}
        position={contextMenuPosition}
        isDemo={false}
      />

      <TableBodyCell modify="basis">
        <ClanMember
          name={account.name}
          hasPermanentBan={hasPermanentBan}
          isHiddenStatistics={application.is_hidden_statistics}
          isOwn={false}
          isHighlightedAdmittance={false}
          disableTooltip={isContextMenuActive}
          role={''}
        />
      </TableBodyCell>

      <TableBodyCell modify="small">
        {comment ? (
          <DivTooltip tooltipBody={<UIKitTooltipBody>{comment}</UIKitTooltipBody>}>
            <span className={styles.letterIcon} />
          </DivTooltip>
        ) : null}
      </TableBodyCell>

      <TableBodyCell modify="small">
        <RankCell
          id={account.id}
          rank={statistics.rank}
          seasonId={statistics.season_id}
          seasonRank={statistics.season_rank}
          disableTooltip={isContextMenuActive}
        />
      </TableBodyCell>

      <TableBodyCell modify="right">
        <StatisticsValue
          value={thousands(statistics.btl)}
          disableTooltip={isContextMenuActive}
          isHidden={isHiddenStatistics}
        />
      </TableBodyCell>

      <TableBodyCell modify="right">
        <StatisticsValue
          value={percent(statistics.wb, undefined)}
          disableTooltip={isContextMenuActive}
          isHidden={isHiddenStatistics}
        />
      </TableBodyCell>

      <TableBodyCell modify="right">
        <StatisticsValue
          value={thousands(statistics.aeb)}
          disableTooltip={isContextMenuActive}
          isHidden={isHiddenStatistics}
        />
      </TableBodyCell>

      <TableBodyCell modify="right">
        <StatisticsValue
          value={thousands(statistics.admg)}
          disableTooltip={isContextMenuActive}
          isHidden={isHiddenStatistics}
        />
      </TableBodyCell>

      <TableBodyCell modify="right">
        <StatisticsValue
          value={floats(statistics.afb, 2)}
          disableTooltip={isContextMenuActive}
          isHidden={isHiddenStatistics}
        />
      </TableBodyCell>

      {actionCell}
    </TableRow>
  );
};

export default memo(ApplicationsClanMemberTableItem);
